import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Search, Package, Link2, Trash2, RefreshCw, CheckCircle2, AlertCircle, Truck, Sparkles, Eye, EyeOff } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CJDropshippingPanel({ tools = [] }) {
  const [status, setStatus] = useState(null);
  const [keyword, setKeyword] = useState('drywall');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [selectedTool, setSelectedTool] = useState('');
  const [pendingProduct, setPendingProduct] = useState(null);
  const [orders, setOrders] = useState([]);
  const [suggesting, setSuggesting] = useState(false);
  const [forwarding, setForwarding] = useState({});
  const [catalog, setCatalog] = useState([]);
  const [catSearch, setCatSearch] = useState('');
  const [togglingId, setTogglingId] = useState('');

  const fetchCatalog = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/tools/admin/catalog`);
      setCatalog(r.data?.tools || []);
    } catch {
      setCatalog([]);
    }
  }, []);

  const toggleVisibility = async (toolId, hidden) => {
    setTogglingId(toolId);
    try {
      await axios.post(`${API}/tools/admin/catalog/${toolId}/visibility`, { hidden });
      setCatalog((prev) => prev.map((t) => (t.id === toolId ? { ...t, hidden } : t)));
      toast.success(hidden ? 'Producto ocultado de la tienda' : 'Producto visible en la tienda');
    } catch (e) {
      toast.error('Error: ' + (e.response?.data?.detail || e.message));
    } finally {
      setTogglingId('');
    }
  };

  const fetchStatus = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/cj/status`);
      setStatus(r.data);
    } catch (e) {
      setStatus({ connected: false, error: e.message });
    }
  }, []);

  const fetchMappings = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/cj/mappings`);
      setMappings(r.data?.mappings || []);
    } catch {
      setMappings([]);
    }
  }, []);

  const fetchOrders = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/orders/?limit=50`);
      setOrders(r.data?.orders || []);
    } catch {
      setOrders([]);
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      await fetchStatus();
      await fetchMappings();
      await fetchOrders();
      await fetchCatalog();
    };
    load();
  }, [fetchStatus, fetchMappings, fetchOrders, fetchCatalog]);

  const handleSearch = async () => {
    setSearching(true);
    try {
      const r = await axios.get(`${API}/cj/search`, {
        params: { keyword, page: 1, page_size: 20 },
      });
      setSearchResults(r.data?.list || []);
    } catch (e) {
      toast.error('Error buscando en CJ: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSearching(false);
    }
  };

  const handleSuggest = async () => {
    if (!selectedTool) {
      toast.error('Selecciona primero una herramienta local arriba.');
      return;
    }
    setSuggesting(true);
    try {
      const r = await axios.get(`${API}/cj/suggest/${selectedTool}`);
      const suggestions = r.data?.suggestions || [];
      setKeyword(r.data?.keyword || keyword);
      setSearchResults(suggestions);
      if (suggestions.length === 0) {
        toast.info('Sin sugerencias automáticas. Prueba una búsqueda manual.');
      } else {
        toast.success(`${suggestions.length} sugerencia(s) para "${r.data?.tool_name}" (búsqueda: "${r.data?.keyword}")`);
      }
    } catch (e) {
      toast.error('Error al auto-sugerir: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSuggesting(false);
    }
  };

  const openMapDialog = async (product) => {
    if (!selectedTool) {
      toast.error('Selecciona primero una herramienta local arriba.');
      return;
    }
    try {
      const r = await axios.get(`${API}/cj/product/${product.pid}`);
      const detail = r.data || {};
      const variants = detail.variants || detail.variantList || [];
      const firstVariant = variants[0] || {};
      setPendingProduct({
        product,
        detail,
        variants,
        selectedVid: firstVariant.vid || firstVariant.variantId || '',
      });
    } catch (e) {
      toast.error('No se pudo cargar el detalle: ' + (e.response?.data?.detail || e.message));
    }
  };

  const confirmMapping = async () => {
    if (!pendingProduct || !pendingProduct.selectedVid) {
      toast.error('Falta variante CJ');
      return;
    }
    const v = pendingProduct.variants.find(
      (x) => (x.vid || x.variantId) === pendingProduct.selectedVid
    ) || {};
    try {
      await axios.post(`${API}/cj/mappings`, {
        tool_id: selectedTool,
        cj_pid: pendingProduct.product.pid,
        cj_vid: pendingProduct.selectedVid,
        cj_product_name: pendingProduct.product.productNameEn || pendingProduct.product.productName,
        cj_sku: v.variantSku || v.sku || '',
        cj_image: v.variantImage || pendingProduct.product.productImage || '',
        cost_usd: parseFloat(v.variantSellPrice || v.sellPrice || 0) || null,
      });
      toast.success('Mapeo guardado');
      setPendingProduct(null);
      fetchMappings();
    } catch (e) {
      toast.error('Error: ' + (e.response?.data?.detail || e.message));
    }
  };

  const deleteMapping = async (tool_id) => {
    if (!window.confirm('¿Eliminar el mapeo?')) return;
    try {
      await axios.delete(`${API}/cj/mappings/${tool_id}`);
      toast.success('Mapeo eliminado');
      fetchMappings();
    } catch (e) {
      toast.error('Error: ' + (e.response?.data?.detail || e.message));
    }
  };

  const forwardOrder = async (orderId) => {
    setForwarding((f) => ({ ...f, [orderId]: true }));
    try {
      const r = await axios.post(`${API}/cj/orders/${orderId}/forward`);
      if (r.data?.status === 'already_forwarded') {
        toast.info('Este pedido ya estaba en CJ: ' + r.data.cj_order_id);
        setForwarding((f) => ({ ...f, [orderId]: false }));
        fetchOrders();
        return;
      }
      toast.info('Enviando a CJ en segundo plano… puede tardar hasta 1 min.', { duration: 4000 });
      // Poll the order until CJ confirms (cj_order_id) or reports an error.
      let tries = 0;
      const poll = setInterval(async () => {
        tries += 1;
        try {
          const res = await axios.get(`${API}/orders/?limit=50`);
          const list = res.data?.orders || [];
          setOrders(list);
          const o = list.find((x) => x.id === orderId);
          if (o && o.cj_order_id) {
            clearInterval(poll);
            setForwarding((f) => ({ ...f, [orderId]: false }));
            toast.success('✅ Pedido enviado a CJ: ' + o.cj_order_id);
          } else if (o && o.cj_status === 'error') {
            clearInterval(poll);
            setForwarding((f) => ({ ...f, [orderId]: false }));
            toast.error('Error al enviar a CJ: ' + (o.cj_error || 'desconocido'));
          } else if (tries >= 30) {
            clearInterval(poll);
            setForwarding((f) => ({ ...f, [orderId]: false }));
            toast.warning('Sigue procesando en CJ. Refresca la página en unos minutos.');
          }
        } catch {
          /* keep polling */
        }
      }, 3000);
    } catch (e) {
      setForwarding((f) => ({ ...f, [orderId]: false }));
      toast.error('Error: ' + (e.response?.data?.detail || e.message));
    }
  };

  const mappedToolIds = new Set(mappings.map((m) => m.tool_id));
  const unmappedTools = tools.filter((t) => !mappedToolIds.has(String(t.id)));
  const ordersPaid = orders.filter((o) => o.status === 'paid' && !o.cj_order_id);
  const ordersInCJ = orders.filter((o) => o.cj_order_id);

  return (
    <div className="space-y-6" data-testid="cj-panel">
      {/* Connection status */}
      <Card className="border-0 shadow-xl">
        <CardHeader className="bg-gradient-to-r from-orange-500 to-amber-600 text-white">
          <CardTitle className="flex items-center gap-3">
            <Package size={24} />
            CJ Dropshipping
            {status?.connected ? (
              <Badge className="bg-green-500 text-white"><CheckCircle2 size={14} className="mr-1" />Conectado</Badge>
            ) : (
              <Badge variant="destructive"><AlertCircle size={14} className="mr-1" />Desconectado</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          {status?.connected ? (
            <div className="text-sm text-slate-600">
              Token válido hasta <strong>{status.expires_at}</strong>.
              <span className="ml-3">{mappings.length} producto(s) mapeado(s) · {unmappedTools.length} pendiente(s).</span>
            </div>
          ) : (
            <div className="text-sm text-red-600">Error: {status?.error}</div>
          )}
        </CardContent>
      </Card>

      {/* Search + map */}
      <Card className="border-0 shadow-xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-slate-800">
            <Search size={20} /> Buscar y mapear productos
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-sm font-semibold text-slate-700">1. Selecciona la herramienta local a mapear:</label>
            <select
              data-testid="cj-tool-select"
              value={selectedTool}
              onChange={(e) => setSelectedTool(e.target.value)}
              className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="">— Elige herramienta —</option>
              {tools.map((t) => (
                <option key={t.id} value={t.id}>
                  {mappedToolIds.has(String(t.id)) ? '✓ ' : '  '}
                  {t.id} — {t.name} (${t.price_usd})
                </option>
              ))}
            </select>
            <Button
              data-testid="cj-suggest-btn"
              onClick={handleSuggest}
              disabled={suggesting || !selectedTool}
              className="mt-2 bg-violet-600 hover:bg-violet-700 text-white"
            >
              {suggesting ? <RefreshCw className="animate-spin" size={16} /> : <Sparkles size={16} />}
              <span className="ml-2">Auto-sugerir productos CJ para esta herramienta</span>
            </Button>
          </div>

          <div>
            <label className="text-sm font-semibold text-slate-700">2. Busca el producto equivalente en CJ:</label>
            <div className="flex gap-2 mt-1">
              <Input
                data-testid="cj-search-input"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="ej: drywall sander, joint knife..."
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
              <Button data-testid="cj-search-btn" onClick={handleSearch} disabled={searching}>
                {searching ? <RefreshCw className="animate-spin" size={16} /> : <Search size={16} />}
                <span className="ml-2">Buscar</span>
              </Button>
            </div>
          </div>

          {searchResults.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-4 max-h-96 overflow-y-auto">
              {searchResults.map((p) => (
                <div key={p.pid} className="border rounded-md p-3 hover:shadow-md transition" data-testid={`cj-result-${p.pid}`}>
                  {p.productImage && (
                    <img src={p.productImage} alt={p.productNameEn} className="w-full h-32 object-contain mb-2" />
                  )}
                  <div className="text-xs font-medium line-clamp-2 min-h-[2.5rem]">{p.productNameEn || p.productName}</div>
                  <div className="text-xs text-slate-500 mt-1">SKU: {p.productSku || '—'}</div>
                  <div className="text-sm font-bold text-green-600 mt-1">${p.sellPrice}</div>
                  <Button
                    size="sm"
                    onClick={() => openMapDialog(p)}
                    className="w-full mt-2 bg-orange-500 hover:bg-orange-600"
                    data-testid={`cj-map-btn-${p.pid}`}
                  >
                    <Link2 size={14} className="mr-1" /> Mapear
                  </Button>
                </div>
              ))}
            </div>
          )}

          {pendingProduct && (
            <div className="border-2 border-orange-400 rounded-lg p-4 bg-orange-50 mt-4">
              <h4 className="font-bold text-orange-900 mb-2">Seleccionar variante para mapear</h4>
              <p className="text-sm mb-2">{pendingProduct.product.productNameEn}</p>
              {pendingProduct.variants.length > 0 ? (
                <select
                  value={pendingProduct.selectedVid}
                  onChange={(e) =>
                    setPendingProduct({ ...pendingProduct, selectedVid: e.target.value })
                  }
                  className="w-full border rounded px-2 py-1 text-sm mb-3"
                  data-testid="cj-variant-select"
                >
                  {pendingProduct.variants.map((v) => (
                    <option key={v.vid || v.variantId} value={v.vid || v.variantId}>
                      {v.variantName || v.variantNameEn || v.variantSku} — ${v.variantSellPrice || v.sellPrice}
                    </option>
                  ))}
                </select>
              ) : (
                <p className="text-sm text-red-600 mb-3">Este producto no tiene variantes disponibles.</p>
              )}
              <div className="flex gap-2">
                <Button onClick={confirmMapping} className="bg-green-600 hover:bg-green-700" data-testid="cj-confirm-mapping-btn">
                  <CheckCircle2 size={16} className="mr-1" /> Confirmar mapeo
                </Button>
                <Button variant="outline" onClick={() => setPendingProduct(null)}>Cancelar</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Existing mappings */}
      <Card className="border-0 shadow-xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-slate-800">
            <Link2 size={20} /> Mapeos actuales ({mappings.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {mappings.length === 0 ? (
            <p className="text-sm text-slate-500">No hay mapeos todavía. Empieza buscando productos arriba.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="text-left px-3 py-2">Tool ID</th>
                    <th className="text-left px-3 py-2">Producto CJ</th>
                    <th className="text-left px-3 py-2">VID</th>
                    <th className="text-left px-3 py-2">Costo</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {mappings.map((m) => (
                    <tr key={m.tool_id} className="border-b" data-testid={`cj-mapping-row-${m.tool_id}`}>
                      <td className="px-3 py-2 font-mono">{m.tool_id}</td>
                      <td className="px-3 py-2">{m.cj_product_name || '—'}</td>
                      <td className="px-3 py-2 font-mono text-xs">{m.cj_vid}</td>
                      <td className="px-3 py-2">${m.cost_usd ?? '—'}</td>
                      <td className="px-3 py-2">
                        <Button size="sm" variant="ghost" onClick={() => deleteMapping(m.tool_id)} data-testid={`cj-delete-mapping-${m.tool_id}`}>
                          <Trash2 size={14} className="text-red-600" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Store visibility */}
      <Card className="border-0 shadow-xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-slate-800">
            <Eye size={20} /> Visibilidad en la tienda
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-500 mb-3">
            Oculta o muestra productos del catálogo en la tienda pública sin borrarlos. Los ocultos
            no aparecen en <span className="font-mono">/tienda</span>.
          </p>
          <Input
            data-testid="catalog-search-input"
            value={catSearch}
            onChange={(e) => setCatSearch(e.target.value)}
            placeholder="Buscar por nombre o ID (ej: TL019, lijadora)..."
            className="mb-3"
          />
          <div className="max-h-96 overflow-y-auto divide-y">
            {catalog
              .filter((t) => {
                const q = catSearch.toLowerCase().trim();
                return !q || t.id.toLowerCase().includes(q) || (t.name || '').toLowerCase().includes(q);
              })
              .map((t) => (
                <div
                  key={t.id}
                  className="flex items-center justify-between gap-3 py-2"
                  data-testid={`catalog-row-${t.id}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    {t.image && <img src={t.image} alt={t.name} className="w-10 h-10 object-contain rounded shrink-0" />}
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">
                        <span className="font-mono text-xs text-slate-400 mr-1">{t.id}</span>
                        {t.name}
                      </div>
                      <div className="text-xs text-slate-500">${t.price_usd} · {t.category}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {t.hidden ? (
                      <Badge variant="secondary" className="bg-slate-200 text-slate-600">Oculto</Badge>
                    ) : (
                      <Badge className="bg-green-100 text-green-700">Visible</Badge>
                    )}
                    <Button
                      size="sm"
                      variant={t.hidden ? 'default' : 'outline'}
                      disabled={togglingId === t.id}
                      onClick={() => toggleVisibility(t.id, !t.hidden)}
                      data-testid={`catalog-toggle-${t.id}`}
                      className={t.hidden ? 'bg-green-600 hover:bg-green-700' : ''}
                    >
                      {togglingId === t.id ? (
                        <RefreshCw size={14} className="animate-spin" />
                      ) : t.hidden ? (
                        <><Eye size={14} className="mr-1" /> Mostrar</>
                      ) : (
                        <><EyeOff size={14} className="mr-1" /> Ocultar</>
                      )}
                    </Button>
                  </div>
                </div>
              ))}
          </div>
        </CardContent>
      </Card>

      {/* Forward orders */}
      <Card className="border-0 shadow-xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-slate-800">
            <Truck size={20} /> Pedidos pagados pendientes de enviar a CJ ({ordersPaid.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {ordersPaid.length === 0 ? (
            <p className="text-sm text-slate-500">No hay pedidos pagados pendientes.</p>
          ) : (
            <div className="space-y-2">
              {ordersPaid.map((o) => (
                <div key={o.id} className="flex items-center justify-between border rounded p-2 text-sm" data-testid={`cj-order-pending-${o.id}`}>
                  <div>
                    <strong>{o.id.slice(0, 8)}</strong> · {o.customer_email} · {o.items?.length || 0} prod · ${o.subtotal}
                  </div>
                  <Button size="sm" disabled={!!forwarding[o.id]} onClick={() => forwardOrder(o.id)} className="bg-orange-500 hover:bg-orange-600" data-testid={`cj-forward-${o.id}`}>
                    {forwarding[o.id] ? (
                      <><RefreshCw size={14} className="mr-1 animate-spin" /> Procesando…</>
                    ) : (
                      <><Truck size={14} className="mr-1" /> Enviar a CJ</>
                    )}
                  </Button>
                </div>
              ))}
            </div>
          )}

          {ordersInCJ.length > 0 && (
            <div className="mt-6">
              <h4 className="font-semibold text-slate-700 mb-2">Pedidos ya enviados a CJ ({ordersInCJ.length})</h4>
              <div className="space-y-1 text-xs">
                {ordersInCJ.map((o) => (
                  <div key={o.id} className="flex justify-between border-b py-1" data-testid={`cj-order-sent-${o.id}`}>
                    <span><strong>{o.id.slice(0, 8)}</strong> → CJ {o.cj_order_id}</span>
                    <Badge>{o.cj_status || 'created'}</Badge>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
